// NPC token spawns and the hidden "fate" scripts that drive each NPC token's life.

import { TUNABLES } from "@degen/shared/config/tunables";
import { TOKEN_WORDS_A, TOKEN_WORDS_B } from "@degen/shared/config/content";
import type { Rng } from "../rng";
import type { GameState } from "../state";
import { pushLog } from "../state";
import { addShill, liveTokens, marketBuy, marketSell } from "./market";
import { balanceOf, createToken, mcapOf, priceOf, type Fate, type Token } from "./token";
import { noteHeldRug } from "../events/random";
import { createPresale, resolvePresale } from "./presale";

const M = TUNABLES.market;
const TPD = TUNABLES.clock.ticksPerDay;

/** Pick today's spawn ticks (sorted). Called at each day boundary. */
export function scheduleSpawns(state: GameState, rng: Rng) {
  const dayStart = state.tick - (state.tick % TPD);
  const n = rng.int(M.spawnPerDay[0], M.spawnPerDay[1]);
  const ticks: number[] = [];
  for (let i = 0; i < n; i++) ticks.push(dayStart + rng.int(0, TPD - 1));
  ticks.sort((a, b) => a - b);
  state.market.spawnTicks = ticks.filter((t) => t >= state.tick);
  // Some of today's launches open a presale draw now (name and ticker reserved in advance).
  for (const t of state.market.spawnTicks) {
    if (t - state.tick < 60 || !rng.chance(TUNABLES.presale.chancePerLaunch)) continue;
    const { name, ticker } = makeName(state, rng);
    createPresale(state, rng, t, name, ticker);
  }
}

function makeName(state: GameState, rng: Rng): { name: string; ticker: string } {
  const used = new Set<string>();
  for (const id in state.market.tokens) used.add(state.market.tokens[id].ticker);
  for (const p of state.market.presales) if (!p.resolved) used.add(p.ticker);
  for (let attempt = 0; attempt < 20; attempt++) {
    const a = rng.pick(TOKEN_WORDS_A);
    const b = rng.pick(TOKEN_WORDS_B);
    const style = rng.int(0, 2);
    const ticker =
      style === 0 ? b.toUpperCase().slice(0, 6)
      : style === 1 ? (a.slice(0, 2) + b.slice(0, 3)).toUpperCase()
      : (a[0] + b).toUpperCase().slice(0, 6);
    if (!used.has(ticker)) return { name: `${a} ${b}`, ticker };
  }
  const n = state.market.nextTokenId;
  return { name: `Token ${n}`, ticker: `TKN${n}`.slice(0, 6) };
}

/** USD (before fee) a dev must spend on a fresh curve to receive `pct`% of supply. */
function devBuyUsd(pct: number): number {
  const c = M.curve;
  const tokens = (pct / 100) * M.totalSupply;
  const net = (c.vUsd0 * c.vTokens0) / (c.vTokens0 - tokens) - c.vUsd0;
  return net / (1 - M.tradeFee);
}

export function spawnNpcToken(state: GameState, rng: Rng, tick: number): Token | null {
  const m = state.market;
  if (liveTokens(m).length >= M.maxActiveTokens) return null;
  const fate = rng.weighted(M.fates) as Fate;
  const presale = m.presales.find((p) => !p.resolved && p.tick === tick);
  const { name, ticker } = presale ?? makeName(state, rng);
  const hypeRange = M.hype.initial[fate];
  const t = createToken({
    id: `t${m.nextTokenId++}`,
    name,
    ticker,
    creator: `npcdev:${rng.int(0, 9999)}`,
    description: `${name}. The community decides.`,
    iconSeed: rng.seed(),
    fate,
    hype: rng.range(hypeRange[0], hypeRange[1]),
    bornTick: tick,
  });
  m.tokens[t.id] = t;
  m.stats.spawned++;

  const bag = M.devBagPct[fate];
  const pct = rng.range(bag[0], bag[1]);
  marketBuy(state, t, t.devId, devBuyUsd(pct));
  t.devBagPct = Math.round(pct * 10) / 10;
  t.hype = rng.range(hypeRange[0], hypeRange[1]); // dev buy shouldn't count as organic hype

  if (fate === "runner") t.moonshot = rng.chance(M.moonshotShare);
  if (t.moonshot) t.moonshotTarget = rng.range(M.fomo.targetMultiple[0], M.fomo.targetMultiple[1]);
  if (fate === "rug") {
    t.rugTick = tick + rng.int(M.rug.delayTicks[0], M.rug.delayTicks[1]);
    t.rugChunksLeft = M.rug.chunks;
  }
  pushLog(state, "token_spawned", `New coin: $${t.ticker} (${t.name})`, { tokenId: t.id });
  if (presale) resolvePresale(state, rng, presale, t);
  return t;
}

/** Per-tick fate behaviour for live NPC tokens: hype decay, dev sells, rugs, runner boosts. */
export function runTokenScripts(state: GameState, rng: Rng, live: Token[]) {
  const tick = state.tick;
  for (const t of live) {
    if (t.creator === "player" || t.creator.startsWith("u:")) {
      // Posting updates (shills) about your own coin halves its hype decay (honest path).
      const at = state.social.playerShills[t.id];
      const updated = at !== undefined && tick - at < TUNABLES.clock.ticksPerDay;
      t.hype *= 1 - M.hype.playerTokenDecayPerTick * (updated ? 0.5 : 1);
      continue;
    }
    if (t.creator.startsWith("protocol:")) {
      t.hype *= 1 - M.hype.decayPerTick.normal;
      continue;
    }
    t.hype *= 1 - M.hype.decayPerTick[t.fate];
    const devBal = balanceOf(t, t.devId);

    switch (t.fate) {
      case "rug":
        if (t.rugTick !== undefined && tick >= t.rugTick && (t.rugChunksLeft ?? 0) > 0) {
          if (!t.rugged) {
            t.rugged = true;
            t.hype = 0;
            state.market.stats.rugged++;
            pushLog(state, "rug", `$${t.ticker} dev just dumped the whole bag. Rugged.`, { tokenId: t.id });
            noteHeldRug(state, t.id);
          }
          const left = t.rugChunksLeft!;
          if (devBal > 0) marketSell(state, t, t.devId, devBal / left);
          t.rugChunksLeft = left - 1;
          t.hype = 0;
        } else if (!t.rugged && rng.chance(M.npcShill.rugChancePerTick)) {
          npcShill(state, rng, t);
        }
        break;
      case "runner": {
        const boost = t.moonshot ? M.hype.moonshotBoost : M.hype.runnerBoost;
        const hot = tick - t.bornTick < boost.forTicks;
        if (hot && rng.chance(boost.chancePerTick)) {
          t.hype = Math.min(100, t.hype + rng.range(boost.amount[0], boost.amount[1]));
        }
        if (rng.chance(M.npcShill.runnerChancePerTick)) npcShill(state, rng, t);
        if (t.moonshot) {
          // Bull-run FOMO: outside money piles in while it's hot, then takes profit and forms the top.
          const W = M.fomo;
          const cap = t.launchPrice * t.totalSupply * (t.moonshotTarget ?? W.targetMultiple[0]);
          if (hot && t.hype > W.minHype && mcapOf(t) < cap && rng.chance(W.buyChancePerTick)) {
            marketBuy(state, t, "crowd", Math.min(W.maxBuyUsd, mcapOf(t) * rng.range(W.buyShare[0], W.buyShare[1])));
          } else if (!hot && (t.holders["crowd"] ?? 0) > 0 && rng.chance(W.sellChancePerTick)) {
            marketSell(state, t, "crowd", (t.holders["crowd"] ?? 0) * W.sellShare);
          }
        }
        break;
      }
      case "normal": {
        if (devBal > 0 && priceOf(t) >= t.launchPrice * M.normalDevSell.minPriceMultiple && rng.chance(M.normalDevSell.chancePerTick)) {
          marketSell(state, t, t.devId, devBal * M.normalDevSell.fraction);
        }
        break;
      }
      case "slow_bleed":
        if (devBal > 0 && (tick - t.bornTick) % M.slowBleedDevSell.everyTicks === 0 && tick > t.bornTick) {
          marketSell(state, t, t.devId, devBal * M.slowBleedDevSell.fraction);
        }
        break;
    }
  }
}

function npcShill(state: GameState, rng: Rng, t: Token) {
  addShill(state.market, {
    tokenId: t.id,
    by: `acct:${rng.int(0, TUNABLES.npc.accounts - 1)}`,
    tick: state.tick,
    strength: rng.range(M.npcShill.strength[0], M.npcShill.strength[1]),
  });
}
